"use client";

import { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";

import { track } from "@vercel/analytics";
import { toast } from "sonner";
import { Loader2, X, Smartphone, RotateCcw, ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { reportError } from "@/lib/report-error";
import { CameraCapture, exitFullscreen } from "@/components/camera-capture";
import { useOrientation } from "@/components/ui/use-orientation";
import { ResultCard, type SearchResult } from "@/components/search-result-card";

interface FreeSearchSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type FlowState = "camera" | "searching" | "results";

export function FreeSearchSheet({
  open,
  onOpenChange,
}: FreeSearchSheetProps) {
  const [flowState, setFlowState] = useState<FlowState>("camera");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { isLandscape } = useOrientation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen) {
      exitFullscreen();
      setFlowState("camera");
      setResults([]);
      setError(null);
    }
    onOpenChange(newOpen);
  }, [onOpenChange]);

  const handleCapture = useCallback(async (imageDataUrl: string) => {
    track("search_executed", { context: "free_search" });
    setFlowState("searching");
    setError(null);

    try {
      const parts = imageDataUrl.split(",");
      if (parts.length !== 2 || !parts[1]) {
        throw new Error("Invalid image data format");
      }
      
      const [header, base64Data] = parts;
      const mimeMatch = header.match(/:(.*?);/);
      const mimeType = mimeMatch ? mimeMatch[1] : "image/jpeg";
      
      let byteString: string;
      try {
        byteString = atob(base64Data);
      } catch {
        throw new Error("Failed to decode image data");
      }
      
      if (byteString.length === 0) {
        throw new Error("Image data is empty");
      }
      
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([uint8Array], { type: mimeType });

      if (blob.size === 0) {
        throw new Error("Failed to create image blob");
      }

      if (blob.size > 20 * 1024 * 1024) {
        throw new Error("Image is too large. Please try again with a simpler scene.");
      }

      const formData = new FormData();
      formData.append("image", blob, "capture.jpg");

      const searchResponse = await fetch("/api/design-search", {
        method: "POST",
        body: formData,
      });

      const data = await searchResponse.json();

      if (!searchResponse.ok) {
        throw new Error(data.error || "Search failed");
      }

      setResults(data.results || []);
      setFlowState("results");
    } catch (err) {
      reportError(err, { component: "FreeSearchSheet", action: "handleCapture" });
      const errorMessage = err instanceof Error ? err.message : "Search failed";
      setError(errorMessage);
      toast.error(errorMessage);
      setFlowState("camera");
    }
  }, []);

  const searchAgain = useCallback(() => {
    track("search_again", { context: "free_search" });
    setResults([]);
    setFlowState("camera");
  }, []);

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent
        className="!inset-0 !top-0 !mt-0 !h-dvh !max-h-dvh !w-full !rounded-none"
        showHandle={false}
        aria-describedby={undefined}
      >
        {!(isLandscape && (flowState === "camera" || flowState === "results")) && (
          <DrawerHeader className="border-b px-4 pb-3 pt-drawer-safe-top">
            <div className="flex items-center gap-2">
              <div className="w-10 shrink-0" aria-hidden />
              <DrawerTitle className="min-w-0 flex-1 text-center text-xl">
                Design Patent Search
              </DrawerTitle>
              <div className="flex w-10 shrink-0 items-center justify-end">
                <button
                  type="button"
                  onClick={() => {
                    if (flowState === "results") track("search_results_closed", { context: "free_search" });
                    handleOpenChange(false);
                  }}
                  className="rounded-full p-2 hover:bg-muted active:bg-muted"
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </button>
              </div>
            </div>
            {flowState === "camera" && (
              <DrawerDescription className="mt-2 text-sm text-muted-foreground">
                Take a photo of any object or drawing
              </DrawerDescription>
            )}
          </DrawerHeader>
        )}

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {flowState === "camera" && (
            <div className="relative flex min-h-0 flex-1 flex-col">
              {isLandscape && (
                <button
                  onClick={() => {
                    handleOpenChange(false);
                  }}
                  className="absolute right-safe-offset-4 top-safe-offset-4 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 active:bg-black/70"
                >
                  <X className="h-6 w-6" />
                  <span className="sr-only">Close</span>
                </button>
              )}
              <CameraCapture
                onCapture={handleCapture}
                isLandscape={isLandscape}
              />
            </div>
          )}

          {flowState === "searching" && (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5 px-6">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
              <p className="text-base text-muted-foreground">Searching patents...</p>
            </div>
          )}

          {flowState === "results" && (
            <>
              {isLandscape ? (
                <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 bg-[hsl(210,40%,98%)] p-8 text-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
                    <Smartphone className="h-10 w-10 -rotate-90 text-primary" strokeWidth={1.5} />
                  </div>
                  <div className="space-y-3">
                    <p className="text-2xl font-bold text-foreground">Rotate to Portrait</p>
                    <p className="text-base leading-relaxed text-muted-foreground">
                      For the best experience viewing results, please rotate your device to portrait mode.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    ref={scrollRef}
                    onScroll={(e) => setShowScrollTop(e.currentTarget.scrollTop > 300)}
                    className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain bg-[hsl(210,40%,98%)]"
                  >
                    {error && (
                      <div className="px-4 py-3 text-center text-sm text-destructive">
                        {error}
                      </div>
                    )}

                    {results.length === 0 ? (
                      <div className="flex flex-col items-center justify-center gap-5 p-8 text-center">
                        <p className="text-base text-muted-foreground">No matching patents found.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                        {(() => {
                          const allScores = results.map(r => r.score);
                          return results.map((result, idx) => (
                            <ResultCard
                              key={result.id}
                              result={result}
                              allScores={allScores}
                              currentIndex={idx}
                            />
                          ));
                        })()}
                      </div>
                    )}

                    <button
                      onClick={() => scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
                      className={cn(
                        "sticky bottom-safe-offset-4 left-1/2 z-10 mx-auto flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all duration-200",
                        showScrollTop ? "scale-100 opacity-100" : "pointer-events-none scale-75 opacity-0"
                      )}
                      aria-label="Scroll to top"
                    >
                      <ArrowUp className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="shrink-0 border-t bg-background px-4 pb-safe-offset-4 pt-4 landscape:px-safe-or-4">
                    <Button
                      variant="outline"
                      size="lg"
                      className="h-12 w-full rounded-xl text-base"
                      onClick={searchAgain}
                    >
                      <RotateCcw className="mr-2 size-5 shrink-0" strokeWidth={1.5} />
                      Search Again
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
